import { AxiosError, AxiosResponse } from "axios";

class ApiError extends Error {
  status?: number;
  response?: any;
  data?: any;

  constructor(message: string, status?: number, response?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
    this.data = response?.data;
  }
}

const handleApiError = (error: unknown): ApiError => {
  if (error instanceof AxiosError) {
    let message = "An error occurred.";
    let status = error.response?.status;

    // Handle Axios error codes
    switch (error.code) {
      case "ERR_NETWORK":
        message =
          error.response?.data?.message ||
          "Network error: Unable to connect to the server. Check your internet connection or if the backend is running.";
        break;
      case "ECONNABORTED":
        message =
          error.response?.data?.message ||
          "Request timed out. The server took too long to respond.";
        break;
      case "ETIMEDOUT":
        message =
          error.response?.data?.message ||
          "The request exceeded the maximum wait time.";
        break;
      case "ERR_BAD_REQUEST":
        message =
          error.response?.data?.message ||
          "Bad request. Please check your input.";
        break;
      case "ERR_BAD_RESPONSE":
        message = error.response?.data?.message || "Invalid server response.";
        break;
      case "ERR_CANCELED":
        message = error.response?.data?.message || "Request was canceled.";
        break;
      case "ERR_FR_TOO_MANY_REDIRECTS":
        message =
          error.response?.data?.message ||
          "Too many redirects. The request could not be completed.";
        break;
      case "ERR_DEPRECATED":
        message =
          error.response?.data?.message ||
          "This API method is deprecated and no longer supported.";
        break;
      case "ERR_NOT_SUPPORT":
        message =
          error.response?.data?.message ||
          "This request method or feature is not supported.";
        break;
      case "ERR_INVALID_URL":
        message =
          error.response?.data?.message || "The request URL is invalid.";
        break;
    }

    // Handle missing response (network failure)
    if (!error.response) {
      if (error.request) {
        return new ApiError(
          "No response from the server. The backend may be down, or there may be a network issue.",
          undefined,
          error.response
        );
      }
      return new ApiError(`Axios Error: ${error.message}`, undefined, error.response);
    }

    // Handle HTTP response status codes
    switch (status) {
      case 400:
        message =
          error.response?.data?.message ||
          `Bad Request (400): ${error.response.data?.message || message}`;
        break;
      case 401:
        message =
          error.response?.data?.message ||
          `Unauthorized (401): Please log in.`;
        break;
      case 403:
        message =
          error.response?.data?.message ||
          `Forbidden (403): You don't have permission to access this resource.`;
        break;
      case 404:
        message =
          error.response?.data?.message ||
          `Not Found (404): The requested resource was not found.`;
        break;
      case 408:
        message =
          error.response?.data?.message ||
          `Request Timeout (408): The server took too long to respond.`;
        break;
      case 409:
        message =
          error.response?.data?.message ||
          `Conflict (409): There was a conflict with your request.`;
        break;
      case 413:
        message =
          error.response?.data?.message ||
          `Payload Too Large (413): The request payload is too large.`;
        break;
      case 415:
        message =
          error.response?.data?.message ||
          `Unsupported Media Type (415): The media type is not supported.`;
        break;
      case 422:
        message =
          error.response?.data?.message ||
          `Unprocessable Entity (422): ${error.response.data?.message || message}.`;
        break;
      case 429:
        message =
          error.response?.data?.message ||
          `Too Many Requests (429): You have made too many requests. Please try again later.`;
        break;
      case 500:
        message =
          error.response?.data?.message ||
          `Internal Server Error (500): Something went wrong on the server.`;
        break;
      case 502:
        message =
          error.response?.data?.message ||
          `Bad Gateway (502): The server received an invalid response.`;
        break;
      case 503:
        message =
          error.response?.data?.message ||
          `Service Unavailable (503): The server is temporarily unavailable.`;
        break;
      case 504:
        message =
          error.response?.data?.message ||
          `Gateway Timeout (504): The server took too long to respond.`;
        break;
      default:
        message = status
          ? `Error ${status}: ${error.response.data?.message || message}`
          : error.response.data?.message || message;
    }
    return new ApiError(message, status, error.response);
  }

  // If it's not an Axios error, return a generic error
  return new ApiError("An unknown error occurred.");
};

export default handleApiError;
